import type { EndpointDTO } from '@devrobot/shared';
import { statusInfo } from './status';

/** Whether an endpoint can be opened over HTTPS right now, and why not when it cannot. */
export function httpsReadiness(e: EndpointDTO): { ready: boolean; reason?: string } {
  if (e.kind !== 'route') return { ready: false, reason: 'direct host port (no HTTPS proxy)' };
  if (e.dns === 'unknown') return { ready: false, reason: 'DNS not checked yet' };
  if (e.dns === 'pending') return { ready: false, reason: 'DNS record does not resolve yet' };
  if (e.dns !== 'ok') return { ready: false, reason: 'DNS record missing or pointing elsewhere' };
  if (e.certificate !== 'valid') return { ready: false, reason: e.certificate === 'failed' ? 'certificate failed' : 'certificate not issued yet' };
  return { ready: true };
}

/** A browser can only open route endpoints; host ports are `tcp://` / `udp://` and are copy-only. */
export const isBrowsable = (e: EndpointDTO) => e.kind === 'route';

/**
 * Orders the Visit menu: hosts this user opened most recently first, then the ones they never
 * opened in the order the deployment declares them.
 */
export function byRecentVisit(a: EndpointDTO, b: EndpointDTO): number {
  if (a.lastVisitedAt && b.lastVisitedAt) return b.lastVisitedAt.localeCompare(a.lastVisitedAt);
  if (a.lastVisitedAt) return -1;
  if (b.lastVisitedAt) return 1;
  return 0;
}

/** Route endpoints first (a browser can open those), then host ports; each group most-recently-visited first. */
export function orderHosts(endpoints: EndpointDTO[]): EndpointDTO[] {
  const browsable = endpoints.filter(isBrowsable).sort(byRecentVisit);
  const rest = endpoints.filter((e) => !isBrowsable(e)).sort(byRecentVisit);
  return [...browsable, ...rest];
}

export type EndpointTone = 'success' | 'warning' | 'error' | 'neutral';

/** One traffic light per endpoint, with the wording a tooltip and screen readers can use. */
export function endpointStatus(e: EndpointDTO): { tone: EndpointTone; label: string; detail?: string } {
  if (e.kind !== 'route') {
    return { tone: 'neutral', label: 'Direct host port', detail: 'Published on the server, not served over HTTPS, and not openable in a browser.' };
  }
  const dns = statusInfo('dns', e.dns);
  const cert = statusInfo('cert', e.certificate);
  const chain = `DNS ${dns.label} · certificate ${cert.label}${e.certificateError ? ` — ${e.certificateError}` : ''}`;
  if (e.dns === 'ok' && e.certificate === 'valid') return { tone: 'success', label: 'HTTPS ready' };
  // Something is actually wrong, as opposed to still settling.
  const broken = e.dns === 'mismatch' || e.certificate === 'failed';
  const reason = httpsReadiness(e).reason ?? '';
  return { tone: broken ? 'error' : 'warning', label: broken ? `Not reachable: ${reason}` : `Not ready yet: ${reason}`, detail: chain };
}

/** The distinguishing head of an endpoint's host, and the tail every sibling repeats. */
export interface EndpointLabel {
  lead: string;
  rest: string;
}

/** How many trailing host labels every one of `hosts` shares, always leaving one in front. */
function sharedTail(hosts: string[][]): number {
  if (hosts.length < 2) return 0;
  let shared = 0;
  for (let i = 1; ; i++) {
    const tail = hosts[0][hosts[0].length - i];
    if (tail === undefined) break;
    if (!hosts.every((h) => h.length > i && h[h.length - i] === tail)) break;
    shared = i;
  }
  return shared;
}

/**
 * Splits each endpoint's host into the part that tells it apart from its siblings and the part
 * they all repeat, so a list of hosts reads as `portal-dev`/`portal-dev-alt` rather than two
 * near-identical URLs. `https://` is dropped: the status light already reports it.
 */
export function endpointLabels(endpoints: EndpointDTO[]): Map<string, EndpointLabel> {
  const out = new Map<string, EndpointLabel>();
  const routes = endpoints.filter(isBrowsable).map((e) => ({ url: e.url, u: new URL(e.url) }));
  const samePort = routes.every((r) => r.u.port === routes[0]?.u.port);
  const shared = samePort ? sharedTail(routes.map((r) => r.u.hostname.split('.'))) : 0;

  for (const { url, u } of routes) {
    const labels = u.hostname.split('.');
    const head = labels.slice(0, labels.length - shared).join('.');
    out.set(url, {
      lead: `${head}${!samePort && u.port ? `:${u.port}` : ''}`,
      rest: `${shared ? `.${labels.slice(-shared).join('.')}` : ''}${samePort && u.port ? `:${u.port}` : ''}`,
    });
  }
  // Host ports keep their scheme and address: there is nothing to shorten against.
  for (const e of endpoints.filter((x) => !isBrowsable(x))) {
    const u = new URL(e.url);
    out.set(e.url, { lead: `${u.protocol}//${u.hostname}`, rest: u.port ? `:${u.port}` : '' });
  }
  return out;
}
